export type ProductInfo = {
  pid: string;
  name: string;
  displayName: string;
  image: string;
  imageURL: string;
  type: string;
  protocolType: string;
  model: string;
  power: string;
  powerUnit: string;
  remarks: string;
  uiPanelId: string;
  version: string;
};

export type Property =
  | {
      type: "bool";
    }
  | {
      type: "value";
      unit: string;
      min: number;
      max: number;
      scale: number;
      step: number;
    }
  | {
      type: "enum";
      range: string[] | { [key: string]: string }[];
    }
  | {
      type: "bitmap";
      label: string[];
      maxlen: number;
    };

export type StandardValue = {
  [key: string]: string;
};

export type BaseStandardFunction = {
  mode: string;
  code: string;
  name: string;
  property: Property;
  id: number;
  codeInternal?: string;
  enabled?: boolean;
  scope?: string;
};

export type PowerFunction = BaseStandardFunction & {
  standardName: "Power";
  standardValues: [
    {
      On: string;
    },
    {
      Off: string;
    }
  ];
};

export type ModeFunction = BaseStandardFunction & {
  standardName: "Mode";
  standardValues: [
    {
      Auto: string;
    },
    {
      Manual: string;
    }
  ];
};

export type SleepFunction = BaseStandardFunction & {
  standardName: "Sleep";
  standardValues: [
    {
      On: string;
    },
    {
      Off: string;
    }
  ];
};

export type SpeedFunction = BaseStandardFunction & {
  standardName: "Speed";
  standardValues: Array<Partial<{ [key in string]: string }>>;
};

export type FilterFunction = BaseStandardFunction & {
  standardName: "filter_life";
  standardValues?: undefined;
};

export type LightFunction = BaseStandardFunction & {
  standardName: "Light";
  standardValues: Array<
    Partial<{ [key in "Low" | "High" | "Auto" | "Sleep"]: string }>
  >;
};

export type LockFunction = BaseStandardFunction & {
  standardName: "Lock";
  standardValues: [
    {
      Lock: string;
    },
    {
      Unlock: string;
    }
  ];
};

export type OtherFunction = BaseStandardFunction & {
  standardName?: string | undefined;
  standardValues?: undefined;
};

export type StandardFunction =
  | PowerFunction
  | SleepFunction
  | SpeedFunction
  | ModeFunction
  | FilterFunction
  | LightFunction
  | LockFunction
  | OtherFunction;

export type FunctionDefinition = {
  standardFunctions: StandardFunction[];
  customFunctions?: StandardFunction[];
  otherFunctions?: {
    schedulingAction: {
      codeInternal: string;
      enabled: boolean;
    }[];
    bulkAction: {
      codeInternal: string;
      enabled: boolean;
    }[];
  };
  cloudFunctions?: StandardFunction[];
};

export type Product = {
  productInfo: ProductInfo;
  functionDefinition: FunctionDefinition;
  sensorInfo: {
    displaySensorName: string;
    displaySensorUnit: string;
    dpId: number;
  }[];
};

export type AlenProductConfigType = {
  products: {
    [key: string]: Product[];
  };
  bulkAction: {
    type: string;
    productIds: string[];
  }[];
  deviceSelection: {
    type: string;
    image: string;
    imageURL: string;
  }[];
  notificationSettings: {
    fieldName: string;
    alertCode: string[];
    defaultValue: string;
    dpId: number;
  }[];
};

// FIXME: this data is from alen device, we will have to fetch this data from the server when we have set up the api in our BE
export const AlenProductConfig: AlenProductConfigType = {
  products: {
    BreatheSmart_75i: [
      {
        productInfo: {
          pid: "tcnmioaUSaLKCNJy",
          name: "BreatheSmart 75i",
          displayName: "BreatheSmart 75i",
          image: "b75i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "1.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "Switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "PM25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Auto", "Sleep"],
                type: "enum",
              },
              id: 3,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Auto", "Manual"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "Auto",
                },
                {
                  Manual: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Sleep"],
                type: "enum",
              },
              id: 3,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "Sleep",
                },
                {
                  Off: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "4",
                  },
                  {
                    "5": "T",
                  },
                ],
                type: "enum",
              },
              id: 4,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  "4": "4",
                },
                {
                  T: "5",
                },
              ],
            },
            {
              mode: "ro",
              code: "Filter",
              name: "Filter Left",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "Anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "Lock",
              name: "Child Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "FilterDays",
              name: "Filter Day Left",
              property: {
                unit: "day",
                min: 0,
                max: 1000,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 16,
            },
            {
              mode: "rw",
              code: "Timer",
              name: "Timer",
              property: {
                range: [
                  {
                    "0": "0",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "4": "4",
                  },
                  {
                    "12": "12",
                  },
                ],
                type: "enum",
              },
              id: 19,
              codeInternal: "TIMER_INT",
              enabled: false,
            },
          ],
          customFunctions: [],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: false,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
        ],
      },
      {
        productInfo: {
          pid: "htjnm3t9smyasvw8",
          name: "BreatheSmart 75i_V2",
          displayName: "BreatheSmart 75i",
          image: "b75i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "2.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "Switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "PM25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Auto", "Sleep"],
                type: "enum",
              },
              id: 3,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Auto", "Manual"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "Auto",
                },
                {
                  Manual: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Sleep"],
                type: "enum",
              },
              id: 3,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "Sleep",
                },
                {
                  Off: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "4",
                  },
                  {
                    "5": "T",
                  },
                ],
                type: "enum",
              },
              id: 4,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  "4": "4",
                },
                {
                  T: "5",
                },
              ],
            },
            {
              mode: "ro",
              code: "Filter",
              name: "Filter Left",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "Anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "Lock",
              name: "Child Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "FilterDays",
              name: "Filter Day Left",
              property: {
                unit: "day",
                min: 0,
                max: 1000,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 16,
            },
            {
              mode: "rw",
              code: "Timer",
              name: "Timer",
              property: {
                range: [
                  {
                    "0": "0",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "4": "4",
                  },
                  {
                    "12": "12",
                  },
                ],
                type: "enum",
              },
              id: 19,
              codeInternal: "TIMER_INT",
              enabled: false,
            },
          ],
          customFunctions: [],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: false,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
        ],
      },
      {
        productInfo: {
          pid: "kajnukffs6appels",
          name: "BreatheSmart 75i",
          displayName: "BreatheSmart 75i v2.0",
          image: "b75i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "2.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "pm25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "mode",
              name: "AutoMode",
              property: {
                range: ["Off", "On"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "On",
                },
                {
                  Manual: "Off",
                },
              ],
            },
            {
              mode: "ro",
              code: "filter_life",
              name: "Filter life",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "Anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "child_lock",
              name: "Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "temp_indoor",
              name: "Indoor temp",
              property: {
                unit: "",
                min: -40,
                max: 130,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 12,
            },
            {
              mode: "ro",
              code: "humidity",
              name: "Indoor humidity",
              property: {
                unit: "",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 13,
            },
          ],
          customFunctions: [
            {
              mode: "ro",
              code: "filter",
              name: "Filter Type",
              property: {
                range: ["Unknown", "PURE", "FRESH", "ODOR"],
                type: "enum",
              },
              id: 106,
            },
            {
              mode: "rw",
              code: "speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "4",
                  },
                  {
                    "5": "T",
                  },
                ],
                type: "enum",
              },
              id: 104,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  "4": "4",
                },
                {
                  T: "5",
                },
              ],
            },
            {
              mode: "rw",
              code: "brightness",
              name: "Light",
              property: {
                range: ["Low", "High", "Auto", "Sleep"],
                type: "enum",
              },
              id: 105,
              standardName: "Light",
              standardValues: [
                {
                  Low: "Low",
                },
                {
                  High: "High",
                },
                {
                  Auto: "Auto",
                },
                {
                  Sleep: "Sleep",
                },
              ],
            },
            {
              mode: "ro",
              code: "co2",
              name: "CO2",
              property: {
                unit: "",
                min: 0,
                max: 9999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 116,
            },
            {
              mode: "ro",
              code: "etoh",
              name: "EtOH",
              property: {
                unit: "",
                min: 0,
                max: 9999,
                scale: 0,
                step: 3,
                type: "value",
              },
              id: 113,
            },
            {
              mode: "rw",
              code: "psleep",
              name: "PSleep",
              property: {
                type: "bool",
              },
              id: 118,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "wynd_airid",
              name: "Wynd_AirID",
              property: {
                unit: "",
                min: 0,
                max: 9999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 120,
            },
          ],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
          {
            displaySensorName: "Temp",
            displaySensorUnit: "°F",
            dpId: 12,
          },
          {
            displaySensorName: "Humidity",
            displaySensorUnit: "%",
            dpId: 13,
          },
          {
            displaySensorName: "eCO2",
            displaySensorUnit: "ppm",
            dpId: 116,
          },
          {
            displaySensorName: "TVOC",
            displaySensorUnit: "ppb",
            dpId: 113,
          },
          {
            displaySensorName: "airID",
            displaySensorUnit: "",
            dpId: 120,
          },
        ],
      },
    ],
    BreatheSmart_45i: [
      {
        productInfo: {
          pid: "Ng7UnT2dUDgIyJL9",
          name: "BreatheSmart 45i",
          displayName: "BreatheSmart 45i",
          image: "b45i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "1.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "Switch",
              name: "Power",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "PM25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Auto", "Sleep"],
                type: "enum",
              },
              id: 3,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Auto", "Manual"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "Auto",
                },
                {
                  Manual: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Sleep"],
                type: "enum",
              },
              id: 3,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "Sleep",
                },
                {
                  Off: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "T",
                  },
                ],
                type: "enum",
              },
              id: 4,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  T: "4",
                },
              ],
            },
            {
              mode: "ro",
              code: "Filter",
              name: "Filter Left",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "Anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "Lock",
              name: "Child Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "TotalTime",
              name: "Total Time",
              property: {
                unit: "minute",
                min: 0,
                max: 5256000,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 17,
            },
            {
              mode: "rw",
              code: "Timer",
              name: "Timer",
              property: {
                range: [
                  {
                    "0": "0",
                  },
                  {
                    "1": "2",
                  },
                  {
                    "2": "4",
                  },
                  {
                    "3": "12",
                  },
                ],
                type: "enum",
              },
              id: 19,
              codeInternal: "TIMER_INT",
              enabled: false,
            },
            {
              mode: "ro",
              code: "Countdown",
              name: "Countdown",
              property: {
                unit: "minute",
                min: 0,
                max: 360,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 20,
            },
            {
              mode: "ro",
              code: "Fault",
              scope: "Fault",
              name: "Fault alarm",
              property: {
                label: ["1", "2"],
                type: "bitmap",
                maxlen: 2,
              },
              id: 21,
            },
          ],
          customFunctions: [],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: false,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
        ],
      },
      {
        productInfo: {
          pid: "1fe3sdugltp4nocc",
          name: "BreatheSmart 45i_V1.5",
          displayName: "BreatheSmart 45i",
          image: "b45i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "2.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "Switch",
              name: "Power",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "PM25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Auto", "Sleep"],
                type: "enum",
              },
              id: 3,
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Auto", "Manual"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "Auto",
                },
                {
                  Manual: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Mode",
              name: "Mode",
              property: {
                range: ["Manual", "Sleep"],
                type: "enum",
              },
              id: 3,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "Sleep",
                },
                {
                  Off: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "Speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "T",
                  },
                ],
                type: "enum",
              },
              id: 4,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  T: "4",
                },
              ],
            },
            {
              mode: "ro",
              code: "Filter",
              name: "Filter Left",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "Anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "Lock",
              name: "Child Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "TotalTime",
              name: "Total Time",
              property: {
                unit: "minute",
                min: 0,
                max: 5256000,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 17,
            },
            {
              mode: "rw",
              code: "Timer",
              name: "Timer",
              property: {
                range: [
                  {
                    "0": "0",
                  },
                  {
                    "1": "2",
                  },
                  {
                    "2": "4",
                  },
                  {
                    "3": "12",
                  },
                ],
                type: "enum",
              },
              id: 19,
              codeInternal: "TIMER_INT",
              enabled: false,
            },
            {
              mode: "ro",
              code: "Countdown",
              name: "Countdown",
              property: {
                unit: "minute",
                min: 0,
                max: 360,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 20,
            },
            {
              mode: "ro",
              code: "Fault",
              scope: "Fault",
              name: "Fault alarm",
              property: {
                label: ["1", "2"],
                type: "bitmap",
                maxlen: 2,
              },
              id: 21,
            },
          ],
          customFunctions: [
            {
              mode: "rw",
              code: "brightness",
              name: "Brightness",
              property: {
                range: ["Half", "Full", "Off"],
                type: "enum",
              },
              id: 101,
              standardName: "Light",
              standardValues: [
                {
                  Auto: "Half",
                },
                {
                  High: "Full",
                },
                {
                  Low: "Off",
                },
              ],
            },
          ],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: false,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
              {
                codeInternal: "TIMER_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
        ],
      },
      {
        productInfo: {
          pid: "gez3obbmv2p2qy7b",
          name: "BreatheSmart 45i",
          displayName: "BreatheSmart 45i v2.0",
          image: "b45i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "2.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "pm25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "mode",
              name: "Mode",
              property: {
                range: ["Manual", "Auto", "Sleep"],
                type: "enum",
              },
              id: 3,
            },
            {
              mode: "rw",
              code: "mode",
              name: "Mode",
              property: {
                range: ["Auto", "Manual"],
                type: "enum",
              },
              id: 3,
              codeInternal: "MODE_INT",
              enabled: true,
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "Auto",
                },
                {
                  Manual: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "mode",
              name: "Mode",
              property: {
                range: ["Manual", "Sleep"],
                type: "enum",
              },
              id: 3,
              codeInternal: "SLEEP_INT",
              enabled: true,
              standardName: "Sleep",
              standardValues: [
                {
                  On: "Sleep",
                },
                {
                  Off: "Manual",
                },
              ],
            },
            {
              mode: "rw",
              code: "anion",
              name: "Ionizer",
              property: {
                type: "bool",
              },
              id: 6,
              codeInternal: "IONIZER_INT",
              enabled: true,
            },
            {
              mode: "rw",
              code: "child_lock",
              name: "Lock",
              property: {
                type: "bool",
              },
              id: 7,
              codeInternal: "LOCK_INT",
              enabled: true,
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "temp_indoor",
              name: "Indoor temp",
              property: {
                unit: "",
                min: -200,
                max: 500,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 12,
            },
            {
              mode: "ro",
              code: "humidity",
              name: "Indoor humidity",
              property: {
                unit: "",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 13,
            },
          ],
          customFunctions: [
            {
              mode: "rw",
              code: "speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "T",
                  },
                ],
                type: "enum",
              },
              id: 104,
              codeInternal: "SPEED_INT",
              enabled: false,
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  T: "4",
                },
              ],
            },
            {
              mode: "rw",
              code: "brightness",
              name: "Brightness",
              property: {
                range: ["Half", "Full", "Auto"],
                type: "enum",
              },
              id: 101,
              standardName: "Light",
              standardValues: [
                {
                  Low: "Half",
                },
                {
                  High: "Full",
                },
                {
                  Auto: "Auto",
                },
              ],
            },
            {
              mode: "ro",
              code: "filter",
              name: "Filter Life",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 106,
              standardName: "filter_life",
            },
            {
              mode: "ro",
              code: "etoh",
              name: "TVOC",
              property: {
                unit: "",
                min: 0,
                max: 9999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 102,
            },
            {
              mode: "ro",
              code: "wynd_airid",
              name: "Wynd_AirID",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 120,
            },
          ],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
          {
            displaySensorName: "Temp",
            displaySensorUnit: "°F",
            dpId: 12,
          },
          {
            displaySensorName: "Humidity",
            displaySensorUnit: "%",
            dpId: 13,
          },
          {
            displaySensorName: "eCO2",
            displaySensorUnit: "ppm",
            dpId: 116,
          },
          {
            displaySensorName: "TVOC",
            displaySensorUnit: "ppb",
            dpId: 113,
          },
        ],
      },
    ],
    BreatheSmart_35i: [
      {
        productInfo: {
          pid: "bd4ztozj4aied1ps",
          name: "BreatheSmart 35i",
          displayName: "BreatheSmart 35i",
          image: "b35i.png",
          imageURL: "",
          type: "Air Purifier",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "3.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              enabled: true,
              codeInternal: "SWITCH_INT",
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "pm25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "rw",
              code: "mode",
              name: "AutoMode",
              property: {
                range: ["On", "Off"],
                type: "enum",
              },
              id: 3,
              enabled: true,
              codeInternal: "MODE_INT",
              standardName: "Mode",
              standardValues: [
                {
                  Auto: "On",
                },
                {
                  Manual: "Off",
                },
              ],
            },
            {
              mode: "ro",
              code: "filter_life",
              name: "Filter life",
              property: {
                unit: "％",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 5,
              standardName: "filter_life",
            },
            {
              mode: "rw",
              code: "child_lock",
              name: "Lock",
              property: {
                type: "bool",
              },
              id: 7,
              enabled: true,
              codeInternal: "LOCK_INT",
              standardName: "Lock",
              standardValues: [
                {
                  Lock: "true",
                },
                {
                  Unlock: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "temp_indoor",
              name: "Indoor temp",
              property: {
                unit: "",
                min: -40,
                max: 130,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 12,
            },
            {
              mode: "ro",
              code: "humidity",
              name: "Indoor humidity",
              property: {
                unit: "",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 13,
            },
          ],
          customFunctions: [
            {
              mode: "rw",
              code: "speed",
              name: "Speed",
              property: {
                range: [
                  {
                    "1": "1",
                  },
                  {
                    "2": "2",
                  },
                  {
                    "3": "3",
                  },
                  {
                    "4": "T",
                  },
                ],
                type: "enum",
              },
              id: 104,
              enabled: false,
              codeInternal: "SPEED_INT",
              standardName: "Speed",
              standardValues: [
                {
                  "1": "1",
                },
                {
                  "2": "2",
                },
                {
                  "3": "3",
                },
                {
                  T: "4",
                },
              ],
            },
            {
              mode: "rw",
              code: "brightness",
              name: "Light",
              property: {
                range: ["Low", "High", "Auto"],
                type: "enum",
              },
              id: 105,
              standardName: "Light",
              standardValues: [
                {
                  Low: "Low",
                },
                {
                  High: "High",
                },
                {
                  Auto: "Auto",
                },
              ],
            },
            {
              mode: "rw",
              code: "psleep",
              name: "PSleep",
              property: {
                type: "bool",
              },
              id: 118,
              enabled: true,
              codeInternal: "SLEEP_INT",
              standardName: "Sleep",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
          ],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: true,
              },
              {
                codeInternal: "MODE_INT",
                enabled: true,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: true,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: true,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: true,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: true,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
          {
            displaySensorName: "Temp",
            displaySensorUnit: "°F",
            dpId: 12,
          },
          {
            displaySensorName: "Humidity",
            displaySensorUnit: "%",
            dpId: 13,
          },
          {
            displaySensorName: "eCO2",
            displaySensorUnit: "ppm",
            dpId: 116,
          },
          {
            displaySensorName: "TVOC",
            displaySensorUnit: "ppb",
            dpId: 113,
          },
        ],
      },
    ],
    AQM: [
      {
        productInfo: {
          pid: "orugk2ttwr2lvuhs",
          name: "AQM",
          displayName: "AQM",
          image: "AQM.png",
          imageURL: "",
          type: "AQM",
          protocolType: "Wi-Fi,Bluetooth LE",
          model: "",
          power: "",
          powerUnit: "W",
          remarks: "",
          uiPanelId: "",
          version: "1.0",
        },
        functionDefinition: {
          standardFunctions: [
            {
              mode: "rw",
              code: "LED",
              name: "LED",
              property: {
                type: "bool",
              },
              id: 105,
            },
            {
              mode: "rw",
              code: "Switch",
              name: "Switch",
              property: {
                type: "bool",
              },
              id: 1,
              codeInternal: "SWITCH_INT",
              enabled: true,
              standardName: "Power",
              standardValues: [
                {
                  On: "true",
                },
                {
                  Off: "false",
                },
              ],
            },
            {
              mode: "ro",
              code: "PM25",
              name: "PM2.5",
              property: {
                unit: "",
                min: 0,
                max: 999,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 2,
            },
            {
              mode: "ro",
              code: "temp_indoor",
              name: "Indoor temp",
              property: {
                unit: "",
                min: -40,
                max: 130,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 12,
            },
            {
              mode: "ro",
              code: "humidity",
              name: "Indoor humidity",
              property: {
                unit: "",
                min: 0,
                max: 100,
                scale: 0,
                step: 1,
                type: "value",
              },
              id: 13,
            },
          ],
          customFunctions: [],
          otherFunctions: {
            schedulingAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: false,
              },
              {
                codeInternal: "MODE_INT",
                enabled: false,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: false,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: false,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: false,
              },
            ],
            bulkAction: [
              {
                codeInternal: "SWITCH_INT",
                enabled: false,
              },
              {
                codeInternal: "MODE_INT",
                enabled: false,
              },
              {
                codeInternal: "SLEEP_INT",
                enabled: false,
              },
              {
                codeInternal: "SPEED_INT",
                enabled: false,
              },
              {
                codeInternal: "IONIZER_INT",
                enabled: false,
              },
              {
                codeInternal: "LOCK_INT",
                enabled: false,
              },
            ],
          },
          cloudFunctions: [],
        },
        sensorInfo: [
          {
            displaySensorName: "PM 2.5",
            displaySensorUnit: "μg/m3",
            dpId: 2,
          },
          {
            displaySensorName: "Temp",
            displaySensorUnit: "°F",
            dpId: 12,
          },
          {
            displaySensorName: "Humidity",
            displaySensorUnit: "%",
            dpId: 13,
          },
        ],
      },
    ],
  },
  bulkAction: [
    {
      type: "BreatheSmart_75i",
      productIds: ["tcnmioaUSaLKCNJy", "htjnm3t9smyasvw8", "kajnukffs6appels"],
    },
    {
      type: "BreatheSmart_45i",
      productIds: ["Ng7UnT2dUDgIyJL9", "1fe3sdugltp4nocc", "gez3obbmv2p2qy7b"],
    },
    {
      type: "BreatheSmart_35i",
      productIds: ["bd4ztozj4aied1ps"],
    },
  ],
  deviceSelection: [
    {
      type: "BreatheSmart_75i",
      image: "b75i",
      imageURL: "",
    },
    {
      type: "BreatheSmart_45i",
      image: "b45i",
      imageURL: "",
    },
    {
      type: "BreatheSmart_35i",
      image: "b35i",
      imageURL: "",
    },
    {
      type: "AQM",
      image: "AQM",
      imageURL: "",
    },
  ],
  notificationSettings: [
    {
      fieldName: "pm25",
      alertCode: ["PM25"],
      defaultValue: "Poor",
      dpId: 2,
    },
    {
      fieldName: "filter_life",
      alertCode: ["FILTER_LIFE"],
      defaultValue: "Replace",
      dpId: 5,
    },
    {
      fieldName: "co2",
      alertCode: ["CO2"],
      defaultValue: "Poor",
      dpId: 116,
    },
    {
      fieldName: "etoh",
      alertCode: ["TVOC"],
      defaultValue: "Poor",
      dpId: 113,
    },
    {
      fieldName: "humidity",
      alertCode: ["HUMIDITY_DRY", "HUMIDITY_HUMID"],
      defaultValue: "Humid",
      dpId: 13,
    },
    {
      fieldName: "temp_indoor",
      alertCode: ["TEMP_INDOOR_HOT", "TEMP_INDOOR_COLD"],
      defaultValue: "Hot",
      dpId: 12,
    },
    {
      fieldName: "wynd_airid",
      alertCode: ["AIR_ID"],
      defaultValue: "DANDER",
      dpId: 120,
    },
  ],
};
export default AlenProductConfig;
